import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getActiveAnnouncement } from '../config/announcements';
import { AUTH_URL } from '../utils/authUrl';
import { useLocalize } from '../i18n/paths';
import '../styles/AnnouncementBanner.scss';

const DISMISS_PREFIX = 'announce:';
const DISMISS_SUFFIX = ':dismissed';

function readDismissed(key) {
  if (!key || typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    // localStorage non disponibile (privacy mode, quota, ecc.): mostra comunque il banner.
    return false;
  }
}

/**
 * Announcement bar promozionale, elemento di flusso normale montato SOPRA
 * HeaderNew (che resta design-intoccabile: nessun markup/stile suo interno
 * viene toccato qui, a parte l'offset additivo descritto sotto).
 *
 * Il banner scorre via con la pagina come un blocco qualsiasi (niente fixed,
 * niente sticky): in cima si vedono banner + header, scorrendo il banner
 * esce di scena e resta solo l'header, con il suo comportamento invariato
 * (transparent in cima → bianco scrollato). Per evitare che l'header
 * (fixed, top:0) copra il banner appena montato, mentre il banner è
 * visibile si tiene aggiornata la custom property CSS `--announce-offset`
 * (letta da HeaderNew.scss come `top`) con "altezza banner - scroll
 * corrente": l'header si aggancia appena sotto il banner e risale in
 * sincrono mentre questo scorre via, fino a top:0 non appena il banner è
 * del tutto fuori vista — un effetto sticky "a cascata" ottenuto senza
 * toccare la posizione fissa esistente dell'header.
 *
 * Quando il menu mobile è aperto il banner sparisce da solo: non ha un suo
 * z-index (resta nel livello "z-index:auto" del flusso), quindi il
 * `.mobile-menu-overlay` di HeaderNew (z-index:999, invariato) lo copre
 * naturalmente, senza bisogno di classi o logica aggiuntiva.
 */
export default function AnnouncementBanner() {
  const { t } = useTranslation();
  const localize = useLocalize();
  const [announcement] = useState(() => getActiveAnnouncement());
  const dismissKey = announcement ? `${DISMISS_PREFIX}${announcement.id}${DISMISS_SUFFIX}` : null;
  const [dismissed, setDismissed] = useState(() => readDismissed(dismissKey));
  const barRef = useRef(null);

  const visible = !!announcement && !dismissed;

  /* useLayoutEffect, non useEffect (causa vera del CLS 0.46 su /about, audit
     13/08, difetto #7): con useEffect la prima misura di --announce-offset
     arriva DOPO il paint (fallback CSS 0px nel frattempo), quindi l'intera
     pagina sotto lo spacer "saltava" giù di ~altezza-banner appena l'effect
     girava: il singolo scarto più grande registrato nel trace (0.070 su
     0.144 totali in una prova con throttling Slow4G+CPU4x, la stessa
     condizione usata da Lighthouse mobile). useLayoutEffect gira in modo
     sincrono PRIMA che il browser dipinga il frame, quindi l'altezza corretta
     è già impostata al primo paint: nessuno scatto, su qualunque pagina (il
     banner è montato una volta sola in LanguageLayout, non solo su /about). */
  useLayoutEffect(() => {
    const root = document.documentElement;

    if (!visible) {
      root.style.removeProperty('--announce-offset');
      return undefined;
    }

    let raf = null;
    const update = () => {
      raf = null;
      // Banner sticky: è SEMPRE in cima al viewport, quindi l'offset per
      // l'header è costante = altezza del banner. (La versione precedente
      // inseguiva lo scroll: serviva quando il banner scorreva via.)
      const height = barRef.current?.offsetHeight ?? 0;
      root.style.setProperty('--announce-offset', `${Math.round(height)}px`);
    };
    const onResize = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('resize', onResize);
    // L'altezza cambia anche senza resize della finestra (font che arrivano,
    // testo su 1 o 2 righe al breakpoint): l'observer tiene l'offset esatto.
    const ro = new ResizeObserver(onResize);
    if (barRef.current) ro.observe(barRef.current);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
      if (raf !== null) window.cancelAnimationFrame(raf);
      root.style.removeProperty('--announce-offset');
    };
  }, [visible]);

  if (!announcement || dismissed) return null;

  const baseKey = announcement.textKey;
  const code = t(`${baseKey}.code`, '');
  const href = announcement.href.startsWith('http')
    ? announcement.href
    : announcement.target === 'site'
      ? localize(announcement.href)
      : `${AUTH_URL}${announcement.href}`;

  const handleDismiss = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDismissed(true);
    try {
      window.localStorage.setItem(dismissKey, '1');
    } catch {
      // Niente persistenza possibile: il banner riapparirà al prossimo giro, non è grave.
    }
  };

  return (
    <>
    {/* Distanziatore: il banner è FIXED (sticky veniva rotto da un antenato),
        questo tiene il suo posto nel flusso così l'hero non finisce coperta. */}
    <div className="announcement-banner-spacer" aria-hidden="true" />
    <div
      ref={barRef}
      className="announcement-banner"
      role="region"
      aria-label={t('announcements.region')}
      style={announcement.bg ? { background: announcement.bg } : undefined}
    >
      <a className="announcement-banner__link" href={href}>
        <p className="announcement-banner__text">
          {t(`${baseKey}.before`)}{' '}
          {code && <span className="announcement-banner__code">{code}</span>}{' '}
          {t(`${baseKey}.after`)}
        </p>
      </a>
      <button
        type="button"
        className="announcement-banner__close"
        onClick={handleDismiss}
        aria-label={t('announcements.close')}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </div>
    </>
  );
}
